/**
 * The approval gate (docs/CONTRACT.md §6, §11). `users.status` is the one field that decides
 * whether the api answers at all, so it is the one field the signed-in surface branches on:
 * `approved` renders the app, `pending` the waiting screen, `rejected`/`suspended` a refusal.
 */
import type { UserStatus } from '@olmt/shared'

/** What the signed-in root layout does with the account. */
export type AccountGate = 'approved' | 'waiting' | 'blocked'

/**
 * The argument is structural, and `status` is optional, because the field can genuinely be absent:
 * a session persisted before §11 carries a `user` without it. That reads as `pending` — the only
 * status that both withholds the app and is worth re-checking, so the first `GET /api/me` settles
 * it (and, after the migration's backfill, settles it as `approved`).
 */
export function accountStatus(user: { status?: UserStatus } | null | undefined): UserStatus {
  return user?.status ?? 'pending'
}

export function accountGate(user: { status?: UserStatus } | null | undefined): AccountGate {
  const status = accountStatus(user)
  if (status === 'approved') return 'approved'
  return status === 'pending' ? 'waiting' : 'blocked'
}
